import { useDispatch } from 'react-redux'
import { useAppSelector } from '../../../app/hooks'
import { StyledAddedToQueueIcon } from '../../../icons/AddedToQueueIcon'
import { StyledAddToQueueIcon } from '../../../icons/AddToQueueIcon'
import { PauseStandardIcon } from '../../../icons/PauseStandardIcon'
import { PlayStandardIcon } from '../../../icons/PlayStandardIcon'
import { StyledRemoveIcon } from '../../../icons/RemoveIcon'
import { IPages } from '../../../shared/types/IPages'
import { mpTrackAddQueue } from '../../../tracking/mixpanel'
import { selectActivePage, selectIsDateEnabled, selectIsQueueEnabled } from '../../application/applicationSlice'
import { popQueuedArticleById, popSuggestedArticleById, pushQueue, selectQueuedArticles } from '../../queue/queueSlice'
import { selectTheme } from '../../theme/data/themeSlice'
import { playOrPauseArticle, selectAudioState, selectCurrentlyPlayingArticle } from '../data/articleSlice'
import { ArticleAddToQueueButton } from '../styled/ArticleAddToQueueButton'
import { ArticleMetadataParagraph } from '../styled/ArticleMetadataParagraph.styled'
import { ArticleQueueRemoveButton } from '../styled/ArticleQueueRemoveButton'
import { ArticleStandardBottomPart } from '../styled/ArticleStandardBottomPart'
import { ArticleStandardContainer } from '../styled/ArticleStandardContainer.styled'
import { ArticleStandardPlayAndTitleContainer } from '../styled/ArticleStandardPlayAndTitleContainer.styled'
import { StyledStandardArticleTitle } from '../styled/ArticleTitle.styled'
import { IArticleTransformed } from '../types/IArticleTransformed'

interface IProps {
    article: IArticleTransformed
    isSuggestedArticle?: boolean
    updateArticleToPlayAfterQueue?: (article: IArticleTransformed) => void
}

export const ArticleStandard = (props: IProps) => {
    const dispatch = useDispatch()

    const audioState = useAppSelector(selectAudioState)
    const currentlyPlayingArticle = useAppSelector(selectCurrentlyPlayingArticle)
    const activePage = useAppSelector(selectActivePage)
    const queuedArticles = useAppSelector(selectQueuedArticles)
    const theme = useAppSelector(selectTheme)
    const isQueueEnabled = useAppSelector(selectIsQueueEnabled)
    const isDateEnabled = useAppSelector(selectIsDateEnabled)

    const onPlayPauseClicked = () => {
        // for queued article there is no pause button in this component
        if (
            props.article.queueId &&
            currentlyPlayingArticle &&
            !currentlyPlayingArticle.queueId &&
            props.updateArticleToPlayAfterQueue
        ) {
            // play this article once all the queued articles are played
            props.updateArticleToPlayAfterQueue(currentlyPlayingArticle)
        }

        dispatch(playOrPauseArticle(props.article))
    }
    const onQueueArticle = () => {
        mpTrackAddQueue(props.article)
        dispatch(pushQueue({ article: props.article, currentlyPlayingArticle }))
    }
    const onPopQueuedArticle = (queuedId?: number) => () => {
        mpTrackAddQueue(props.article)
        dispatch(popQueuedArticleById(queuedId || props.article.queueId))
    }
    const onPopSuggestedArticle = (queuedId?: number) => () =>
        dispatch(popSuggestedArticleById(queuedId || props.article.queueId))

    //-/ !props.article.queueId: to hide the pause button for a queued article when the same non-queued article is playing(in queue tab)
    //-/ !currentlyPlayingArticle.queueId: to hide the pause button for a non-queued article when the same queued article is playing(in trending articles tab)
    const showPauseIcon =
        currentlyPlayingArticle != null &&
        audioState === 'playing' &&
        !props.article.queueId &&
        !currentlyPlayingArticle.queueId &&
        currentlyPlayingArticle.id === props.article.id

    const sameQueuedArticle = queuedArticles.find(qArticle => qArticle.id === props.article.id)

    return (
        <ArticleStandardContainer>
            <ArticleStandardPlayAndTitleContainer>
                {showPauseIcon ? (
                    <PauseStandardIcon
                        height={32}
                        width={32}
                        smallHeight={24}
                        smallWidth={24}
                        onClick={onPlayPauseClicked}
                    />
                ) : (
                    <PlayStandardIcon
                        smallHeight={24}
                        smallWidth={24}
                        height={32}
                        width={32}
                        onClick={onPlayPauseClicked}
                    />
                )}
                <StyledStandardArticleTitle>{props.article.name}</StyledStandardArticleTitle>
            </ArticleStandardPlayAndTitleContainer>
            <ArticleStandardBottomPart>
                <ArticleMetadataParagraph color={theme.articleDateAndLengthColor}>
                    {isDateEnabled ? `${props.article.date} • ` : ''}
                    {props.article.audioLength.text}
                </ArticleMetadataParagraph>

                {activePage !== IPages.QUEUE && isQueueEnabled && !sameQueuedArticle && (
                    <ArticleAddToQueueButton onClick={onQueueArticle}>
                        <StyledAddToQueueIcon />
                    </ArticleAddToQueueButton>
                )}

                {activePage !== IPages.QUEUE && isQueueEnabled && !!sameQueuedArticle && (
                    <ArticleAddToQueueButton onClick={onPopQueuedArticle(sameQueuedArticle?.queueId!)}>
                        <StyledAddedToQueueIcon />
                    </ArticleAddToQueueButton>
                )}

                {activePage === IPages.QUEUE && (
                    <ArticleQueueRemoveButton
                        onClick={props.isSuggestedArticle ? onPopSuggestedArticle() : onPopQueuedArticle()}>
                        <StyledRemoveIcon />
                    </ArticleQueueRemoveButton>
                )}
            </ArticleStandardBottomPart>
        </ArticleStandardContainer>
    )
}
